"use client"
import { useForm } from "react-hook-form"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Textarea } from "@/shared/components/ui/textarea"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form"
import { Send } from "lucide-react"
import type { ContactFormData } from "@/shared/types/Forms"
import { useState } from "react"
import { useToast } from "@/shared/components/Toast"

export const ContactFormSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()

  const form = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      bestTimeToCall: "",
      message: "",
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: data.name,
        email: data.email,
        subject: data.subject,
        best_time_to_call: data.bestTimeToCall || "",
        phone: data.phone || "",
        message: data.message
      }
      const response = await fetch('/api/v2/forms.php?type=contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json()

      if (result.success) {
        // Show toast notification
        showToast({
          type: 'success',
          title: 'Message Sent!',
          message: 'Your message has been sent successfully. We\'ll get back to you soon!',
          duration: 5000
        })
        
        form.reset() // Clear the form on success
      } else {
        // Show error toast
        showToast({
          type: 'error',
          title: 'Submission Failed', 
          message: result.error || result.message || 'Failed to send message. Please try again.',
          duration: 5000
        })
      }
    } catch (error) {
      console.error('Contact form error:', error)
      const errorMessage = 'Failed to send message. Please try again later or contact us directly at info@twi.com.ph'
      
      // Show error toast
      showToast({
        type: 'error',
        title: 'Error',
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-in fade-in duration-700">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm mb-4 font-semibold shadow-sm">
            <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
            We'd Love to Hear From You
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Send Us a Message
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form and we'll get back to you within 24 hours
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 lg:p-10 border border-gray-100 card-interactive animate-smooth-fade-in" style={{ animationDelay: '200ms' }}>
          <Form {...form}>
            <form id="contact-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ required: "Full name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-900 text-sm">Full Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 h-11 rounded-lg transition-smooth focus-ring text-base"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-900 text-sm">Email Address *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 h-11 rounded-lg transition-smooth focus-ring text-base"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-900 text-sm">Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="+63 917 123 4567"
                          className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 h-11 rounded-lg text-base"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bestTimeToCall"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-900 text-sm">Best Time to Call</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="e.g., 2:00 PM"
                          className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 h-11 rounded-lg text-base"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="subject"
                rules={{ required: "Subject is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-900 text-sm">Subject *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., General Inquiry"
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 h-11 rounded-lg text-base"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                rules={{ required: "Message is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-900 text-sm">Message *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us how we can help you..."
                        rows={6}
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg text-base"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  variant="ghost"
                  disabled={isSubmitting}
                  className="btn-autumn-primary h-12 rounded-lg font-bold uppercase tracking-wide transition-all duration-300 px-8 sm:px-10 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)'
                    e.currentTarget.style.color = 'white'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
                    e.currentTarget.style.color = 'white'
                  }}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </section>
  )
}
